import type { Locale } from 'date-fns';
import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';

type Props = {
  currentMonth: Date;
  locale: Locale;
  minDate: Date;
  maxDate: Date;
  onChange: (monthIndex: number) => void;
};

export function MonthSelect({ currentMonth, locale, minDate, maxDate, onChange }: Props) {
  const year = currentMonth.getFullYear();

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const label = new Intl.DateTimeFormat(locale.code, { month: 'long' }).format(
        new Date(year, i, 1)
      );
      const disabled =
        (year === minDate.getFullYear() && i < minDate.getMonth()) ||
        (year === maxDate.getFullYear() && i > maxDate.getMonth());
      return {
        index: i,
        label: label.charAt(0).toUpperCase() + label.slice(1),
        disabled,
      };
    });
  }, [year, locale.code, minDate, maxDate]);

  return (
    <Select
      value={String(currentMonth.getMonth())}
      onValueChange={v => onChange(Number(v))}>
      <SelectTrigger
        size="default"
        withChevron
        placeholder=""
        className="w-auto min-w-[11rem] cursor-pointer gap-[0.4rem] pl-[1.6rem] pr-[1.2rem] transition-colors focus-visible:ring-0 focus-visible:border-transparent hover:bg-stroke-bg data-[state=open]:bg-stroke-bg [&>svg]:text-secondary">
        <span className="headline4 text-primary">
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent
        className="max-h-[32rem] w-auto max-w-none min-w-[var(--radix-select-trigger-width)] rounded-[1.6rem]"
        classNameViewport="w-auto min-w-full max-w-none">
        {months.map(({ index, label, disabled }) => (
          <SelectItem
            key={index}
            value={String(index)}
            disabled={disabled}
            showOptionCheckmark={false}
            className="headline4 cursor-pointer py-[1.4rem] px-[2.4rem]">
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
